import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { login as loginApi, register as registerApi } from "../api/auth";

function decodeJWT(token: string): Record<string, unknown> {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

function isAdminFromToken(token: string | null): boolean {
  if (!token) return false;
  return !!decodeJWT(token)["is_admin"];
}

interface AuthState {
  token: string | null;
  isAdmin: boolean;
  user: { id: number; email: string } | null;
  isLoading: boolean;
  error: string | null;
  message: string | null;
}

const storedToken = localStorage.getItem("token");

const initialState: AuthState = {
  token: storedToken,
  isAdmin: isAdminFromToken(storedToken),
  user: null,
  isLoading: false,
  error: null,
  message: null,
};

export const login = createAsyncThunk(
  "auth/login",
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await loginApi({ email, password });
      return response.data; // { token }
    } catch (err: any) {
      if (err.response?.data?.error) {
        return rejectWithValue(err.response.data.error);
      }
      return rejectWithValue(err.message);
    }
  },
);

export const register = createAsyncThunk(
  "auth/register",
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await registerApi({ email, password });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || "auth.registerError");
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.isAdmin = false;
      state.user = null;
      localStorage.removeItem("token");
    },
    clearMessages: (state) => {
      state.error = null;
      state.message = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.isAdmin = isAdminFromToken(action.payload);
      localStorage.setItem("token", action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.isAdmin = isAdminFromToken(action.payload.token);
        localStorage.setItem("token", action.payload.token);
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || "auth.loginError";
      })
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false;
        state.message = "auth.registerSuccess";
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || "auth.registerError";
      });
  },
});

export const { logout, clearMessages, setToken } = authSlice.actions;
export default authSlice.reducer;
